import { Component, computed, effect, inject, input, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { PersonGridItemObject, SecondaryDiagnosisObject, VoucherGridItemObject } from '@it2go/types';
import { ContactTypeEnum } from '../../../../../../../../types/src/common/enum/contact-type.enum';
import { SubscriptionComponent } from '../../../../shared/helpers/subscription.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { combineLatest, merge, of } from 'rxjs';
import { ConfirmationService, PrimeTemplate } from '@openng/optimus-ui/api';
import { VoucherDetailGeneralStore } from './voucher-detail-general.store';
import { VoucherDetailService } from '../../../pages/person/voucher-detail-page/voucher-detail-page.service';
import { TableColumnType } from '../../../../shared/components/kit/table/table.types';
import {
    VoucherSecondaryDiagnosisReportingTypeEnum,
    voucherSecondaryDiagnosisReportingTypeEnumOptions,
} from '@libs/types/src/common/enum/voucher-secondary-diagnosis-reporting-type.enum';
import { insuranceTypeEnumOptions } from '../../../../../../../../types/src/common/enum/insurance-type.enum';
import { getValid } from '@libs/shared/utils/get-valid';
import { dateify } from '../../../../shared/helpers/date.helper';
import { PersonInsuranceObject } from '@libs/types/src/services/object-type/person/person-insurance.object';
import { LocalDatePipe } from '../../../../shared/pipes/local-date.pipe';
import { VoucherDetailGeneralStore2 } from './voucher-detail-general.store2';
import { AppFormGroup, changeSignal, enableFormControls } from '../../../../../utils/form.utils';
import {
    PersonContactUpsertDialogComponent,
} from '../person-contact-upsert-dialog/person-contact-upsert-dialog.component';
import { isAllowed, routeParams, translate } from '../../../../shared/shared.global-service';
import {
    ServiceDoctorSelectInputComponent,
} from '../../../../../components/medical-service/element/service-doctor-select-input/service-doctor-select-input.component';
import { VoucherSequenceInfo } from '../../../pages/person/voucher-detail-page/voucher-detail-page.types';
import { BaseDialogService } from '../../../../../components/kit/base-dialog/base-dialog-service';
import { contentGlobalWarnings } from '../../../content.error';
import { AsyncPipe } from '@angular/common';
import { TextInputComponent } from '../../../../../components/kit/input/text-input/text-input.component';
import { SelectInputComponent } from '../../../../../components/kit/input/select-input/select-input.component';
import {
    InsuranceCompanySelectInputComponent,
} from '../../../../../components/medical-service/element/insurance-company-select-input/insurance-company-select-input.component';
import { DateInputComponent } from '../../../../../components/kit/input/date-input/date-input.component';
import { ButtonComponent } from '../../../../../components/kit/button/button.component';
import { Tooltip } from '@openng/optimus-ui/tooltip';
import { NumberInputComponent } from '../../../../../components/kit/input/text-input/number-input.component';
import {
    DiagnosisSelectInputComponent,
} from '../../../../../components/medical-service/diagnosis-select-input/diagnosis-select-input.component';
import { TableComponent } from '../../../../shared/components/kit/table/table.component';
import { Select } from '@openng/optimus-ui/select';
import { FormsModule } from '@angular/forms';
import { Chip } from '@openng/optimus-ui/chip';
import { WarnFloatMessageComponent } from '../../../../../components/kit/float-message/warn-float-message.component';
import { LinkButtonComponent } from '../../../../../components/kit/button/link-button.component';
import { ErrorFloatMessageComponent } from '../../../../../components/kit/float-message/error-float-message.component';
import { FormatVoucherPipe } from '../../../../shared/pipes/format-voucher.pipe';
import { ConfirmPopup } from '@openng/optimus-ui/confirmpopup';
import { FolderGlobalStore } from '../../../../../store/medicalService/folder.global-store';
import {
    isDiagnosisBonified,
} from '../../../pages/person/voucher-detail-page/voucher-detail-page.utils';

@Component({
    selector: 'app-voucher-detail-general',
    templateUrl: 'voucher-detail-general.component.html',
    styleUrls: ['voucher-detail-general.component.scss'],
    providers: [VoucherDetailGeneralStore, VoucherDetailGeneralStore2],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        AsyncPipe,
        TextInputComponent,
        SelectInputComponent,
        InsuranceCompanySelectInputComponent,
        DateInputComponent,
        ServiceDoctorSelectInputComponent,
        ButtonComponent,
        Tooltip,
        TranslatePipe,
        NumberInputComponent,
        DiagnosisSelectInputComponent,
        TableComponent,
        PrimeTemplate,
        Select,
        FormsModule,
        Chip,
        WarnFloatMessageComponent,
        LinkButtonComponent,
        ErrorFloatMessageComponent,
        FormatVoucherPipe,
        ConfirmPopup,
    ],
})
export class VoucherDetailGeneralComponent extends SubscriptionComponent implements OnInit {

    public readonly store2 = inject(VoucherDetailGeneralStore2);
    protected readonly folderStore = inject(FolderGlobalStore);
    protected readonly store = inject(VoucherDetailGeneralStore);
    private readonly confirmationService = inject(ConfirmationService);
    private readonly dialogService = inject(BaseDialogService);
    private readonly translate = inject(TranslateService);
    private readonly detailService = inject(VoucherDetailService);
    private readonly localDatePipe = inject(LocalDatePipe);


    voucherSecondaryDiagnosisReportingTypeEnumOptions = voucherSecondaryDiagnosisReportingTypeEnumOptions;

    voucher = input<VoucherGridItemObject | null>(null);
    @ViewChild(ServiceDoctorSelectInputComponent) doctorSelectInput!: ServiceDoctorSelectInputComponent;

    person: PersonGridItemObject | null = null;
    insuranceMismatch = false;
    insuranceNotExists = false;

    voucherIsBilled = computed(() => this.voucher()?.isBilled || false);

    protected newInsuranceFromDate = computed(() => {
        const now = new Date();
        const januaryFirst = new Date(now.getFullYear(), 0, 1);
        const julyFirst = new Date(now.getFullYear(), 6, 1);

        return now > julyFirst ? julyFirst : januaryFirst;
    });

    protected datesOverlay: boolean = false;
    protected nextVoucher: VoucherGridItemObject | null = null;

    protected form: AppFormGroup = this.detailService.generalForm;
    protected formSecondaryDiagnoseeChanged = changeSignal<string>(this.form.controls['addSecondaryDiagnosis'] as any);

    protected columns = [
        {
            name: 'reporting',
        },
        {
            name: 'code',
            dataKey: 'diagnosis.code',
        },
        {
            name: 'name',
            dataKey: 'diagnosis.name',
        },
        {
            name: 'bonified',
            dataKey: 'diagnosis.bonified',
            type: TableColumnType.Bool,
        },
    ];

    private validInsurance: PersonInsuranceObject | null = null;

    constructor() {
        super();

        effect(() => {
            enableFormControls(this.form, this.folderStore.canEdit());
        });

        effect(() => {
            const actSecDiagnose = this.formSecondaryDiagnoseeChanged();
            this.addSecondaryDiagnosis(actSecDiagnose);
        });
    }

    ngOnInit() {
        this.subs.push(
            this.store.person$.subscribe((person) => {
                this.person = person;
                if (person && !this.voucher()) {
                    this.validInsurance = getValid(this.person?.insurances, new Date(), 'insured') || null;
                    this.detailService.patchGeneralForm({
                        insuranceNumber: this.person?.insuranceNumber || null,
                        insuranceAbbr: this.validInsurance?.insuranceAbbr || null,
                        insuranceType: this.validInsurance?.insuranceType || null,
                        insuredSince: dateify(this.validInsurance?.insuredFrom),
                    });
                }
            }),
            this.form.valueChanges.subscribe(() => {
                this.validInsurance = getValid(this.person?.insurances, this.form.value.issueDate || new Date(), 'insured') || null;
                this.insuranceMismatch = this.validInsurance !== null && (
                    this.form.value.insuranceType != this.validInsurance.insuranceType ||
                    this.form.value.insuranceAbbr != this.validInsurance.insuranceAbbr ||
                    this.form.value.insuredSince?.toISOString() != this.validInsurance.insuredFrom);

                this.insuranceNotExists =
                    !this.person?.insurances?.length &&
                    this.form.getRawValue().insuranceNumber &&
                    this.form.value.insuranceType &&
                    this.form.value.insuranceAbbr &&
                    this.form.value.insuredSince;
            }),
            combineLatest([
                merge(of(this.form.controls.doctorId.value), this.form.controls.doctorId.valueChanges),
                merge(of(this.form.controls.issueDate.value), this.form.controls.issueDate.valueChanges),
                merge(of(this.form.controls.validTo.value), this.form.controls.validTo.valueChanges),
                merge(of(this.form.controls.closureDate.value), this.form.controls.closureDate.valueChanges),
            ]).subscribe(async ([doctorId, issueDate, validTo, closure]) => {
                if (doctorId && issueDate && validTo && !routeParams.voucherId()) {
                    const lastVoucher = await this.detailService.getLastVoucher(
                        doctorId,
                        issueDate,
                        this.voucher()?.id || null,
                    );
                    const nextVoucher = await this.detailService.getNextVoucher(
                        doctorId,
                        issueDate,
                        this.voucher()?.id || null,
                    );
                    this.nextVoucher = nextVoucher;

                    const info: VoucherSequenceInfo = this.detailService.getVoucherSequenceInfo(
                        issueDate,
                        closure || validTo,
                        lastVoucher,
                        nextVoucher,
                    );

                    this.datesOverlay = !info.sequenceNumber;
                    this.form.controls.sequenceNumber.setValue(info.sequenceNumber);
                    this.form.controls.voucherPrevId.setValue(info.lastVoucherId);
                }
            }),
        );
    }

    addSecondaryDiagnosis(event: string) {
        if (!event) return;

        const code: string = event;
        if (
            code &&
            !this.form?.value?.secondaryDiagnoses?.find(
                (it: SecondaryDiagnosisObject) => it.diagnosis.code == code,
            )
        ) {
            combineLatest([
                this.store.getDiagnosis(code),
                this.store.serviceExpertise$,
            ]).subscribe(([diagnosis, expertise]) => {
                const diag = diagnosis['items'][0];
                const bonified = isDiagnosisBonified(
                    diag.bonifications,
                    expertise,
                    this.form.value.issueDate,
                );
                this.form.patchValue({
                    secondaryDiagnoses: [
                        ...(this.form.value.secondaryDiagnoses || []),
                        {
                            reporting:
                                this.form.value.secondaryDiagnoses.filter(
                                    (x: SecondaryDiagnosisObject) =>
                                        x.reporting ===
                                        VoucherSecondaryDiagnosisReportingTypeEnum.TO_VOUCHER_AND_BATCH,
                                ).length < 4
                                    ? VoucherSecondaryDiagnosisReportingTypeEnum.TO_VOUCHER_AND_BATCH
                                    : VoucherSecondaryDiagnosisReportingTypeEnum.NOT_REPORTED,
                            diagnosis: {
                                code: diag.code,
                                name: diag.name || diag.code,
                                bonified: bonified,
                            } as any,
                        } as any,
                    ],
                    addSecondaryDiagnosis: null,
                }, { isCodeUpdate: true });
            });
        } else {
            contentGlobalWarnings.next('voucher.messages.secondaryDiagnosesExists');
            this.form.patchValue({ addSecondaryDiagnosis: null }, { isCodeUpdate: true });
        }
    }

    createPersonInsurance(event: Event, notExists: boolean) {
        this.confirmationService.confirm({
            target: event.target as EventTarget,
            message: this.translate.instant(
                'voucher.messages.insuranceReallyCreateInsurance',
                { insuranceFrom: this.localDatePipe.transform(this.newInsuranceFromDate(), 'dd.MM.yyyy') },
            ),
            acceptLabel: this.translate.instant('yes'),
            rejectLabel: this.translate.instant('no'),
            key: 'insuranceConfirm',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                if (this.person) {

                    if (notExists) {
                        this.store.api.updatePerson({
                            personId: this.person.id,
                            insuranceNumber: this.form.value.insuranceNumber,
                        });
                    } else {
                        const lastEndDate = new Date(this.newInsuranceFromDate());
                        lastEndDate.setDate(lastEndDate.getDate() - 1);
                        lastEndDate.setHours(23, 59, 59, 999);
                        this.store.api.updatePersonInsurance({
                            id: this.validInsurance?.id || 0,
                            insuredTo: lastEndDate.toISOString(),
                        });
                    }

                    this.store.api.createPersonInsurance({
                        personId: Number(this.person.id),
                        insuranceAbbr: this.form.value.insuranceAbbr,
                        insuranceType: this.form.value.insuranceType,
                        insuredFrom: this.newInsuranceFromDate().toISOString(),
                        insuredTo: null,
                    });

                    this.detailService.patchGeneralForm({
                        insuranceAbbr: this.form.value.insuranceAbbr,
                        insuranceType: this.form.value.insuranceType,
                        insuredSince: this.newInsuranceFromDate(),
                    });

                    this.insuranceMismatch = false;
                }
            },
            reject: () => {
            },
        });
    }

    updateInsurance(event: Event) {
        this.confirmationService.confirm({
            target: event.target as EventTarget,
            message: this.translate.instant(
                'voucher.messages.insuranceReallyUpdateVoucherInsurance',
            ),
            acceptLabel: this.translate.instant('yes'),
            rejectLabel: this.translate.instant('no'),
            key: 'insuranceConfirm',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                if (this.person) {
                    this.detailService.patchGeneralForm({
                        insuranceAbbr: this.validInsurance?.insuranceAbbr || null,
                        insuranceType: this.validInsurance?.insuranceType || null,
                        insuredSince: dateify(this.validInsurance?.insuredFrom),
                    });
                }
            },
            reject: () => {
            },
        });
    }

    deleteSecondaryDiagnosis(diagnosis: SecondaryDiagnosisObject) {
        this.detailService.patchGeneralForm({
            secondaryDiagnoses: this.form.value.secondaryDiagnoses.filter(
                (it: SecondaryDiagnosisObject) => {
                    return it.diagnosis.code != diagnosis.diagnosis.code;
                },
            ),
        });
    }

    addNewDoctor(e: any) {
        const ref = this.dialogService.open(
            PersonContactUpsertDialogComponent,
            {
                header: translate.instant('database.persons.contacts.addHeader'),
                data: {
                    type: ContactTypeEnum.doctor,
                    typeDisabled: true,
                },
                styleClass: 'p-dialog-width-60',
            },
        );

        ref?.onClose.subscribe((res) => {
            if (res && res.userId && res.refresh) {
                this.doctorSelectInput?.forceRefresh?.set({});
                this.form.patchValue({ doctorId: res.userId });
            }
        });
    }

    protected readonly insuranceTypeEnumOptions = insuranceTypeEnumOptions;
    protected readonly isAllowed = isAllowed;
}
